import {
  Button,
  Chip,
  Container,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useQueryClient } from 'react-query';
import DownloadIcon from '@mui/icons-material/Download';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { PROFORMAS_KEY, PROFORMA_DETAILS_KEY } from '../../api/hooks/cache-keys';
import { useGetProformaDetails, useUpdateProformaStatus } from '../../api/hooks/sales';
import DICTIONARY from '../../utils/dictionary';
import { accountToString, toPersianDate, toPersianNumber } from '../../utils/helpers';
import { STATIC_URL } from '../../api/requests/urls';
import { useGetUser } from '../../api/hooks/auth';

export default function PerformaDetailsForm({ id }) {
  const queryClient = useQueryClient();
  const { data: proforma } = useGetProformaDetails(id);
  const { data: creator } = useGetUser(proforma?.creatorId, { enabled: !!proforma?.creatorId });
  const updateState = useUpdateProformaStatus();
  const processedRows = processRows(proforma.rows);
  const aggregatedData = aggregateData(processedRows);
  async function handleUpdateStatus(status) {
    await updateState.mutateAsync({ id, status });
    await queryClient.invalidateQueries(PROFORMA_DETAILS_KEY(id));
    await queryClient.invalidateQueries(PROFORMAS_KEY());
  }
  return (
    <Container sx={{ width: { xs: '300px', md: '1200px' } }}>
      <Typography variant="h6">پیش فاکتور شماره {toPersianNumber(proforma.number)}</Typography>
      <Typography color="text.secondary">{`توسط: ${creator?.fullName ?? 'ثبت‌نشده'}`}</Typography>
      <Grid container sx={{ py: 2 }} spacing={2}>
        <Grid item xs={6} md={4}>
          <TextField
            value={proforma?.seller?.name}
            size="small"
            label="فروشنده"
            focused
            fullWidth
            inputProps={{ readOnly: true }}
          />
        </Grid>
        <Grid item xs={6} md={4}>
          <TextField
            value={proforma?.buyer?.name}
            size="small"
            label="خریدار"
            focused
            fullWidth
            inputProps={{ readOnly: true }}
          />
        </Grid>
        <Grid item xs={6} md={2}>
          <TextField
            value={toPersianDate(proforma?.createdAt)}
            size="small"
            label="تاریخ صدور"
            focused
            fullWidth
            inputProps={{ readOnly: true }}
          />
        </Grid>
        <Grid item xs={6} md={2}>
          <TextField
            value={proforma?.paymentDestinations?.map(accountToString).join(' یا ')}
            size="small"
            label="حساب مقصد"
            focused
            fullWidth
            inputProps={{ readOnly: true }}
          />
        </Grid>

        <Grid item xs={12} md={4} alignSelf="center">
          <Chip label={DICTIONARY[proforma?.status]} />
        </Grid>
        <Grid item xs={12} md={8} justifyContent="start">
          <Stack direction="row-reverse" spacing={2}>
            {proforma.status === 'APPROVED' && (
              <>
                <Button variant="contained" onClick={() => handleUpdateStatus('COMPLETED')}>
                  تکمیل فروش
                </Button>
                <Button
                  variant="outlined"
                  target="_blank"
                  href={`/sales/proforma/${id}`}
                  endIcon={<OpenInNewIcon />}
                >
                  پیش‌فاکتور
                </Button>
              </>
            )}
            {proforma.status === 'COMPLETED' && (
              <>
                <Button
                  variant="outlined"
                  target="_blank"
                  href={`/sales/proforma/${id}`}
                  endIcon={<OpenInNewIcon />}
                >
                  فاکتور
                </Button>
                <Button
                  variant="outlined"
                  target="_blank"
                  href={`${STATIC_URL}/delivery/delivery-${proforma?.id}.xlsx`}
                  endIcon={<DownloadIcon />}
                >
                  اکسل تحویل
                </Button>
              </>
            )}

            {proforma.status !== 'REJECTED' && proforma.status !== 'COMPLETED' && (
              <Button
                variant="outlined"
                color="error"
                onClick={() => handleUpdateStatus('REJECTED')}
              >
                لغو
              </Button>
            )}
            {(proforma.status === 'PENDING' || proforma.status === 'REJECTED') && (
              <Button variant="outlined" onClick={() => handleUpdateStatus('APPROVED')}>
                تایید
              </Button>
            )}
          </Stack>
        </Grid>
        <Grid item xs={12}>
          <TableContainer sx={{ maxHeight: { xs: 200, md: 300 } }}>
            <Table stickyHeader aria-label="sticky table" sx={{ maxHeight: { xs: 200, md: 300 } }}>
              <TableHead>
                <TableRow>
                  {columns.map((column) => (
                    <TableCell
                      key={column.id}
                      align={column.align}
                      style={{ minWidth: column.minWidth, fontWeight: '600' }}
                    >
                      {column.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {processedRows?.map((row) => {
                  return (
                    <TableRow hover tabIndex={-1} key={row.name + row.id}>
                      {columns.map((column) => {
                        const value = row[column.id];
                        return (
                          <TableCell key={column.id} align={column.align}>
                            {column.format ? column.format(value) : value}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })}
                <TableRow hover>
                  <TableCell />
                  <TableCell />
                  <TableCell />
                  <TableCell>{toPersianNumber(aggregatedData.totalAmount)}</TableCell>
                  <TableCell>{toPersianNumber(aggregatedData.totalPrice, true)}</TableCell>
                </TableRow>
                <TableRow hover>
                  <TableCell />
                  <TableCell />
                  <TableCell />
                  <TableCell>تخفیف</TableCell>
                  <TableCell>{toPersianNumber(proforma.discount, true)}</TableCell>
                </TableRow>
                <TableRow hover>
                  <TableCell />
                  <TableCell />
                  <TableCell />
                  <TableCell>مبلغ نهایی</TableCell>
                  <TableCell>
                    {toPersianNumber(aggregatedData.totalPrice - proforma.discount, true)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
        <Grid item xs={12}>
          <TextField
            value={proforma?.details}
            size="small"
            label="توضیحات"
            focused
            fullWidth
            inputProps={{ readOnly: true }}
          />
        </Grid>
      </Grid>
    </Container>
  );
}

function processRows(rows) {
  return rows?.map((r) => ({ ...r, totalPrice: Number(r.amount) * Number(r.unitPrice) }));
}

function aggregateData(rows) {
  const totalPrice = Math.floor(rows?.reduce((p, c) => p + c.totalPrice * 1.10, 0));
  const totalAmount = rows?.reduce((p, c) => p + Number(c.amount), 0);
  return { totalPrice, totalAmount };
}

const columns = [
  {
    id: 'model',
    label: 'مدل',
    minWidth: 100,
    format: (model) => `${model.name} (${model.ram}/${model.memory})`,
  },
  { id: 'color', label: 'رنگ', minWidth: 70, format: (color) => color.name },
  {
    id: 'unitPrice',
    label: 'قیمت واحد (ریال)',
    minWidth: 70,
    format: (p) => toPersianNumber(Math.floor(1.10 * p), true),
  },
  { id: 'amount', label: 'تعداد', minWidth: 70, format: toPersianNumber },
  {
    id: 'totalPrice',
    label: 'قیمت کل (ریال)',
    minWidth: 70,
    format: (p) => toPersianNumber(Math.floor(1.10 * p), true),
  },
];
